import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Loader2, HeartHandshake, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const err = (e, fb) => e.response?.data?.detail || fb;

const StatusBadge = ({ t }) => {
  if (t.status === 'open') return <Badge data-testid={`ssr-ticket-status-${t.id}`}>Open</Badge>;
  return (
    <Badge variant="secondary" className={t.outcome === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} data-testid={`ssr-ticket-status-${t.id}`}>
      Closed · {t.outcome === 'approved' ? 'Approved' : 'Denied'}
    </Badge>
  );
};

const CloseDialog = ({ ticket, onClose, onDone }) => {
  const [outcome, setOutcome] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOutcome(''); }, [ticket]);
  const save = async () => {
    setBusy(true);
    try { onDone((await axios.post(`${API}/supportive-service-tickets/${ticket.id}/status`, { status: 'closed', outcome })).data); onClose(); toast.success('Ticket closed'); }
    catch (e) { toast.error(err(e, 'Could not close the ticket')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ticket} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ssr-close-dialog">
        <DialogHeader>
          <DialogTitle>Close {ticket?.number}</DialogTitle>
          <DialogDescription>Was the {ticket?.support_type} support approved or denied? The participant sees this outcome in their request list.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button variant={outcome === 'approved' ? 'default' : 'outline'} onClick={() => setOutcome('approved')} data-testid="ssr-close-approved">Approved</Button>
          <Button variant={outcome === 'denied' ? 'destructive' : 'outline'} onClick={() => setOutcome('denied')} data-testid="ssr-close-denied">Denied</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!outcome || busy} data-testid="ssr-close-confirm">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Close Ticket</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TUITION_LABELS = [
  ['institution_name', 'Institution name'], ['institution_address', 'Institution address'], ['institution_city_state_zip', 'Institution city, state and zip'],
  ['program_name', 'Course, program or concentration'], ['enrollment_date', 'Enrollment date'], ['program_start_date', 'Program start date'],
  ['payee_name', 'Payee name'], ['total_tuition', 'Total tuition amount'], ['payee_address', 'Payee address'], ['payee_city_state_zip', 'Payee city, state and zip'],
];

const TicketDetails = ({ t }) => (
  <div className="text-sm space-y-2">
    {t.mailing_address && (
      <p data-testid={`ssr-ticket-address-${t.id}`}><span className="text-muted-foreground">Mailing address:</span> {t.mailing_address}
        {t.address_updated && <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300">Updated by participant</Badge>}</p>
    )}
    {t.tuition && (
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-muted/40 p-3" data-testid={`ssr-ticket-tuition-${t.id}`}>
        {TUITION_LABELS.map(([k, l]) => <p key={k}><span className="text-muted-foreground">{l}:</span> {t.tuition[k]}</p>)}
      </div>
    )}
    {t.internet_amount && (
      <p data-testid={`ssr-ticket-amount-${t.id}`}><span className="text-muted-foreground">Amount requested:</span> <span className="font-semibold">{t.internet_amount}</span></p>
    )}
    {(t.documents || []).length > 0 && (
      <div className="flex flex-wrap gap-2" data-testid={`ssr-ticket-docs-${t.id}`}>
        {t.documents.map((d) => (
          <Button key={d.document_id} asChild size="sm" variant="outline" data-testid={`ssr-ticket-doc-${d.kind}-${t.id}`}>
            <Link to={`/cases/${t.case_id}/documents/${d.document_id}/view`}><FileText className="w-4 h-4 mr-1" />{d.label}</Link>
          </Button>
        ))}
      </div>
    )}
  </div>
);

const TicketRow = ({ t, onChange, onClose }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { onChange((await axios.post(`${API}/supportive-service-tickets/${t.id}/notes`, { note })).data); setNote(''); }
    catch (e) { toast.error(err(e, 'Could not add the note')); } finally { setBusy(false); }
  };
  const reopen = async () => {
    try { onChange((await axios.post(`${API}/supportive-service-tickets/${t.id}/status`, { status: 'open' })).data); toast.success('Ticket reopened'); }
    catch (e) { toast.error(err(e, 'Could not reopen the ticket')); }
  };
  return (
    <div className="p-4 space-y-3" data-testid={`ssr-ticket-row-${t.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{t.number} · {t.support_type}</span>
            <StatusBadge t={t} />
            {t.status === 'open' && t.business_days_open != null && (
              <Badge variant="outline" data-testid={`ssr-ticket-age-${t.id}`}>
                Open {t.business_days_open} business {t.business_days_open === 1 ? 'day' : 'days'}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Requested {t.request_date} · Assigned to {t.assigned_staff_name || 'Unassigned'}{t.closed_at ? ` · Closed ${fmt(t.closed_at)} by ${t.closed_by_name}` : ''}</p>
          <p className="text-sm mt-2 whitespace-pre-wrap" data-testid={`ssr-ticket-statement-${t.id}`}>{t.statement}</p>
          <div className="mt-2"><TicketDetails t={t} /></div>
        </div>
        {t.status === 'open'
          ? <Button size="sm" variant="outline" onClick={() => onClose(t)} data-testid={`ssr-ticket-close-${t.id}`}>Close Ticket</Button>
          : <Button size="sm" variant="outline" onClick={reopen} data-testid={`ssr-ticket-reopen-${t.id}`}>Reopen</Button>}
      </div>
      {(t.notes || []).length > 0 && (
        <div className="border-l-2 pl-3 space-y-2" data-testid={`ssr-ticket-notes-${t.id}`}>
          {t.notes.map((n) => (
            <div key={n.id} className="text-sm">
              <p className={n.system ? 'italic text-muted-foreground' : 'whitespace-pre-wrap'}>{n.text}</p>
              <p className="text-xs text-muted-foreground">{n.by_name} · {fmt(n.at)}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 items-start">
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a staff note" data-testid={`ssr-ticket-note-input-${t.id}`} />
        <Button size="sm" onClick={addNote} disabled={busy || !note.trim()} data-testid={`ssr-ticket-note-add-${t.id}`}>{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Add Note</Button>
      </div>
    </div>
  );
};

export const SupportiveServiceTicketsTab = ({ caseId }) => {
  const [tickets, setTickets] = useState(null);
  const [closing, setClosing] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/cases/${caseId}/supportive-service-tickets`).then((r) => setTickets(r.data)).catch((e) => toast.error(err(e, 'Could not load tickets')));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  const replace = (t) => setTickets((list) => list.map((x) => (x.id === t.id ? t : x)));
  return (
    <Card className="card-shadow" data-testid="ssr-tickets-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><HeartHandshake className="w-5 h-5" />Supportive Service Request Tickets</CardTitle>
        <CardDescription>Requests the participant sent from the HELPConnect Resources Center. Closing a ticket records whether the support was approved or denied; the participant is not sent a follow-up.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {!tickets ? <Loader2 className="w-6 h-6 animate-spin m-6" /> : tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6" data-testid="ssr-tickets-empty">No supportive service requests yet.</p>
        ) : (
          <div className="divide-y">{tickets.map((t) => <TicketRow key={t.id} t={t} onChange={replace} onClose={setClosing} />)}</div>
        )}
      </CardContent>
      <CloseDialog ticket={closing} onClose={() => setClosing(null)} onDone={replace} />
    </Card>
  );
};
